import { useCallback, useEffect, useState } from 'react';

/**
 * A router in thirty lines.
 *
 * The app has five screens and no need for nested layouts or data loaders, so
 * the History API is the whole dependency. The server rewrites unknown paths to
 * index.html, which is what makes a shared /host/ABCD12 link work on reload.
 */

export type Route =
  | { name: 'home' }
  | { name: 'join'; code: string }
  | { name: 'play' }
  | { name: 'practice' }
  | { name: 'teacher' }
  | { name: 'host'; code: string };

export function parseRoute(path: string): Route {
  const parts = path.replace(/^\/+|\/+$/g, '').split('/');
  const [head, tail] = parts;
  switch (head) {
    case 'join': return { name: 'join', code: (tail ?? '').toUpperCase() };
    case 'play': return { name: 'play' };
    case 'practice': return { name: 'practice' };
    case 'teacher': return { name: 'teacher' };
    case 'host': return { name: 'host', code: (tail ?? '').toUpperCase() };
    default: return { name: 'home' };
  }
}

export function navigate(path: string): void {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function useRoute(): [Route, (path: string) => void] {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));
  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const go = useCallback((path: string) => navigate(path), []);
  return [route, go];
}
